import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, Button } from "@realtyflows/ui";
import { getNextActions } from "../api";

/**
 * NextBestActions — V2 Feature 2.6
 *
 * The agent's "what should I do next" engine, displayed prominently on
 * the Dashboard. Pulls a ranked list from the backend:
 *   - Hot clients gone silent
 *   - Stalled folders
 *   - Folders with missing required docs
 *   - Upcoming/overdue milestones
 *   - Unsigned envelopes past 48h
 *
 * Each item has a one-click primary action. Clicking either navigates
 * or pops the AI email composer (depending on action.kind).
 *
 * Props:
 *   workspace          — current workspace context
 *   onDraftEmail({clientId, intent}) — called when an action requests AI draft
 */
export default function NextBestActions({ workspace, onDraftEmail }) {
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("nba_dismissed") || "[]"); } catch { return []; }
  });

  useEffect(() => {
    const params = { limit: 6 };
    if (workspace) params.workspace = workspace;
    getNextActions(params)
      .then((rows) => setActions(rows || []))
      .catch(() => setActions([]))
      .finally(() => setLoading(false));
  }, [workspace]);

  const visible = actions.filter((a) => !dismissed.includes(a.id));
  if (loading || visible.length === 0) return null;

  const handleAction = (action) => {
    if (action.primaryAction.kind === "draft_email" && onDraftEmail) {
      onDraftEmail({
        clientId: action.primaryAction.clientId,
        intent: action.primaryAction.intent,
        action,
      });
    } else if (action.primaryAction.kind === "navigate") {
      navigate(action.primaryAction.url);
    }
  };

  const handleDismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    sessionStorage.setItem("nba_dismissed", JSON.stringify(next));
  };

  return (
    <Panel padding={20} style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
            ✨ Next Best Actions
          </span>
          <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
            What to tackle first today
          </span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {visible.slice(0, 5).map((action) => {
          const priorityColor = action.priority >= 80 ? t.coral : action.priority >= 60 ? t.amber : t.sky;
          return (
            <div key={action.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "10px 12px",
              background: t.surfaceWarm,
              border: `1px solid ${t.border}`,
              borderRadius: 8,
              borderLeft: `3px solid ${priorityColor}`,
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{action.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>
                  {action.title}
                </div>
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, fontFamily: font.body }}>
                  {action.subtitle}
                </div>
              </div>
              <Button variant="primary" size="sm" onClick={() => handleAction(action)}>
                {action.primaryAction.label}
              </Button>
              <button
                onClick={() => handleDismiss(action.id)}
                aria-label="Dismiss"
                title="Dismiss for this session"
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  color: t.textMuted, fontSize: 14, padding: "0 4px", fontWeight: 700,
                }}
              >×</button>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
